<script lang='ts'>
    import { afterUpdate, onMount, onDestroy } from 'svelte';
    import { addDays, LocalDate } from '@/time';
    import LoadingAnimation from "./LoadingAnimation.svelte";
    import TrainingStatus from '../TrainingStatus.svelte';
    import { resize_observer_content_box } from '@/Util/ResizeObserverSingleton';
    import { debouncer } from '@/Util/utils';

    export let date: Date; // start of the week
    let endDate = addDays(date, 6); // end of the week
    let formattedDateRange = date.toLocaleString('en-us', {month: 'short'}) + ' ' + date.getDate()
        + (date.getMonth() != endDate.getMonth() ?
            ('-' + endDate.toLocaleString('en-us', {month: 'short'}) + ' ') : '-')
        + endDate.getDate();
    export let displayedStats;
    export let loadedStats;
    export let isMobile = false;
    export let forceShowDate = false;
    let mobileExpanded: boolean = false;

    let stats = null
    $: if (loadedStats) stats = loadedStats[LocalDate.format(date)]

    let scrollArrowsVisible: boolean = false;
    let atStart: boolean = false; // true if stats scroll position is at the beginning
    let atEnd: boolean = false; // true if stats scroll position is at the end
    let statsWidth: number = 0;
    let statsRow: HTMLElement|null = null;


    // TODO: remove once svelte releases resize observer bindings.
    let resizeObserverDispose;
    const resizeDebouncer = debouncer(100)
    onMount(()=>{
        resizeObserverDispose = resize_observer_content_box.observe(statsRow, (e)=>{
        const w = e.contentBoxSize[0].inlineSize;
            resizeDebouncer.debounce(()=>statsWidth = w)
        });
    });
    onDestroy(()=>{
        if (resizeObserverDispose) resizeObserverDispose();
    });

    $: if (statsWidth > 0) updateScrollArrows();
    
    function updateScrollArrows() {
        if (!statsRow) return;
        let hasOverflow = statsRow.scrollWidth > statsWidth;
        if (scrollArrowsVisible != hasOverflow) {
            scrollArrowsVisible = hasOverflow;
        }
        atStart = statsRow.scrollLeft == 0;
        atEnd = statsRow.scrollLeft + statsRow.offsetWidth >= statsRow.scrollWidth
    }

    function getLoadedStats(stats: any, attribute: string) {
        if (stats) {
            return stats[attribute] ?? '-';
        }
        return '-';
    }
</script>

<div class='stats-container relative' class:mobile={isMobile}>
    <div class='stats-row hidden-scrollbar' class:mobile={isMobile} class:compressed={!mobileExpanded} on:scroll={updateScrollArrows} bind:this={statsRow}>
        {#if stats}
            <h5 class='stat'><span class='font-bold text-xs'>Weekly Stats{isMobile || forceShowDate ? ' - ' + formattedDateRange : ''}</span></h5>
            <div class='m-auto px-3'><div class="w-0 h-7 border border-solid border-neutral-200"/></div>
            <h5 class='stat'><span class='text-xs flex flex-row items-center'><TrainingStatus trainingAdvice={stats} {date} showDate={true} showColour={false} enableFade={false} showTooltip={false} isCalendar={true}/></span></h5>
            {#if displayedStats}
                {#each displayedStats as s}
                    {#if !isMobile}
                        <!-- Divider -->
                        <div class='m-auto px-3'><div class="w-0 h-7 border border-solid border-neutral-200"/></div>
                    {/if}
                    <h5 class='stat'>{s.label}: {@html (getLoadedStats(stats, s.value))}</h5>
                {/each}
            {/if}
        {:else}
            <LoadingAnimation/>
        {/if}
    </div>
    {#if isMobile}
        <button class='side-button absolute top-0 left-0'
                on:click={() => {mobileExpanded = !mobileExpanded;}}>
            <i class='fa-solid fa-xl fa-caret-{mobileExpanded ? 'up' : 'down'}' aria-hidden='true'/>
        </button>
    {:else}
        <button class='side-button absolute top-0 left-0' class:hidden-button={!scrollArrowsVisible}
                on:click={() => {statsRow.scrollBy({left: -statsWidth*3/4, behavior: 'smooth'});}}>
            <i class='fa-{atStart ? 'thin' : 'regular'} fa-xl fa-chevron-left' aria-hidden='true'/>
        </button>
        <button class='side-button absolute top-0 right-0' class:hidden-button={!scrollArrowsVisible}
                on:click={() => {statsRow.scrollBy({left: statsWidth*3/4, behavior: 'smooth'});}}>
            <i class='fa-{atEnd ? 'thin' : 'regular'} fa-xl fa-chevron-right' aria-hidden='true'/>
        </button>
    {/if}
</div>

<style lang="scss">
    .stats-container.mobile {
        margin-top: 13px;
        margin-bottom: 16px;
        border: 1px solid #ECECEC;
    }
    .side-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 44px;
        background-color: #F9F9F9;
        opacity: 0.9;
        border: none;
    }
    .hidden-button {
        visibility: hidden;
    }
    .stats-row {
        padding-left: 44px;
        padding-right: 44px;
        flex-grow: 1;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        min-height: 44px;
        overflow-x: auto;
        background-color: #F9F9F9;
    }
    .stats-row.mobile {
        align-items: center;
        flex-wrap: wrap;
        text-align: center;
        justify-content: unset;
        &.compressed {
            height: 44px;
            overflow: hidden;
        }
    }
    .stat {
        margin: auto;
        padding-left: 12px;
        padding-right: 12px;
        font-size: 12px;
        color: #3D3D3D;
        white-space: nowrap;
        text-align: center;
        font-weight: 400;
    }
    .mobile .stat {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        align-items: center;
        height: 44px;
        border-bottom: 1px solid #E4E4E4;
        flex-grow: 1;
    }
    .hidden-scrollbar {
        -ms-overflow-style: none; /* for Internet Explorer, Edge */
        scrollbar-width: none; /* for Firefox */
    }
    .hidden-scrollbar::-webkit-scrollbar {
        display: none; /* for Chrome, Safari, and Opera */
    }
</style>